import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useOfflineStorage } from "@/hooks/useOfflineStorage";
import { prepareFileForStorage, canStoreOffline, compressImageIfNeeded, MAX_OFFLINE_FILE_SIZE, formatFileSize } from "@/utils/offlineFileHandler";

export type TipoDocumentoSocial = 
  | 'soporte' 
  | 'acta' 
  | 'lista_asistencia' 
  | 'foto' 
  | 'video' 
  | 'audio' 
  | 'informe' 
  | 'correspondencia_entrada' 
  | 'correspondencia_salida' 
  | 'pqrs' 
  | 'otros';

export interface DocumentoSocial {
  id: string;
  tipo_documento: TipoDocumentoSocial;
  nombre_documento: string;
  descripcion?: string;
  url_archivo: string;
  tamano_archivo?: number;
  tipo_archivo?: string;
  fecha_documento?: string;
  metadata?: any;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export const useDocumentosSociales = (tipoDocumento?: TipoDocumentoSocial) => {
  const [documentos, setDocumentos] = useState<DocumentoSocial[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { cacheData, getCachedData, addPendingOperation, isOnline } = useOfflineStorage();

  const loadDocumentos = async () => {
    if (!user) return;

    const cacheKey = `documentos_sociales_${user.id}_${tipoDocumento || 'all'}`;

    try {
      setLoading(true);

      if (!isOnline) {
        const cached = await getCachedData('documentos', cacheKey);
        if (cached) {
          setDocumentos(cached);
        }
        return;
      }

      let query = supabase
        .from('documentos_sociales')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (tipoDocumento) {
        query = query.eq('tipo_documento', tipoDocumento);
      }

      const { data, error } = await query;

      if (error) throw error;

      const docs = (data || []) as DocumentoSocial[];
      setDocumentos(docs);
      await cacheData('documentos', cacheKey, docs);
    } catch (error: any) {
      console.error('Error al cargar documentos:', error);
      
      const cached = await getCachedData('documentos', cacheKey);
      if (cached) {
        setDocumentos(cached);
        toast({
          title: "Modo offline",
          description: "Mostrando documentos guardados localmente",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los documentos",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadDocumento = async (
    file: File,
    nombreDocumento: string,
    tipo: TipoDocumentoSocial,
    descripcion?: string,
    fechaDocumento?: Date,
    metadata?: any
  ): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para subir documentos",
        variant: "destructive",
      });
      return false;
    }

    // Comprimir imágenes si es necesario
    let processedFile = file;
    if (file.type.startsWith('image/')) {
      processedFile = await compressImageIfNeeded(file);
    }

    // Validar tamaño para modo offline
    if (!isOnline && !canStoreOffline(processedFile)) {
      toast({
        title: "Archivo muy grande",
        description: `Para subir archivos mayores a ${formatFileSize(MAX_OFFLINE_FILE_SIZE)}, conéctate a internet`,
        variant: "destructive",
      });
      return false;
    }

    try {
      setUploading(true);
      const timestamp = Date.now();
      const tempId = `temp_${timestamp}`;

      if (!isOnline) {
        // Modo offline
        const fileData = await prepareFileForStorage(processedFile);
        if (!fileData) {
          throw new Error('No se pudo preparar el archivo');
        }

        const tempDoc: DocumentoSocial = {
          id: tempId,
          user_id: user.id,
          tipo_documento: tipo,
          nombre_documento: nombreDocumento,
          descripcion,
          url_archivo: 'pending',
          tamano_archivo: processedFile.size,
          tipo_archivo: processedFile.type,
          fecha_documento: fechaDocumento?.toISOString().split('T')[0],
          metadata: metadata || {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        await addPendingOperation('create', 'documentos_sociales', {
          ...tempDoc,
          fileData,
        });

        setDocumentos(prev => [tempDoc, ...prev]);

        toast({
          title: "Documento guardado offline",
          description: "Se subirá cuando te conectes a internet",
        });

        return true;
      }

      // Modo online
      const fileExt = processedFile.name.split('.').pop();
      const fileName = `${user.id}/${timestamp}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documentos-sociales')
        .upload(fileName, processedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documentos-sociales')
        .getPublicUrl(fileName);

      const { error: dbError } = await supabase
        .from('documentos_sociales')
        .insert({
          user_id: user.id,
          tipo_documento: tipo,
          nombre_documento: nombreDocumento,
          descripcion,
          url_archivo: publicUrl,
          tamano_archivo: processedFile.size,
          tipo_archivo: processedFile.type,
          fecha_documento: fechaDocumento?.toISOString().split('T')[0],
          metadata: metadata || {}
        });

      if (dbError) throw dbError;

      toast({
        title: "Éxito",
        description: "Documento subido correctamente",
      });

      loadDocumentos();
      return true;
    } catch (error: any) {
      console.error('Error al subir documento:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo subir el documento",
        variant: "destructive",
      });
      return false;
    } finally {
      setUploading(false);
    }
  };

  const deleteDocumento = async (documento: DocumentoSocial): Promise<boolean> => {
    if (!user) return false;

    try {
      if (!isOnline) {
        await addPendingOperation('delete', 'documentos_sociales', {
          id: documento.id,
        });

        setDocumentos(prev => prev.filter(d => d.id !== documento.id));

        toast({
          title: "Documento marcado para eliminar",
          description: "Se eliminará cuando te conectes a internet",
        });

        return true;
      }

      // Modo online
      const url = new URL(documento.url_archivo);
      const filePath = url.pathname.split('/').slice(-2).join('/');

      const { error: storageError } = await supabase.storage
        .from('documentos-sociales')
        .remove([filePath]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documentos_sociales')
        .delete()
        .eq('id', documento.id);

      if (dbError) throw dbError;

      toast({
        title: "Éxito",
        description: "Documento eliminado correctamente",
      });

      loadDocumentos();
      return true;
    } catch (error: any) {
      console.error('Error al eliminar documento:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el documento",
        variant: "destructive",
      });
      return false;
    }
  };

  const getEstadisticas = () => {
    const tipos: { [key: string]: number } = {};
    
    documentos.forEach(doc => {
      tipos[doc.tipo_documento] = (tipos[doc.tipo_documento] || 0) + 1;
    });

    return {
      total: documentos.length,
      porTipo: tipos,
      tamanioTotal: documentos.reduce((acc, doc) => acc + (doc.tamano_archivo || 0), 0),
    };
  };

  useEffect(() => {
    if (user) {
      loadDocumentos();
    }
  }, [user, tipoDocumento]);

  // Suscripción en tiempo real
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('documentos-sociales-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documentos_sociales',
          filter: `user_id=eq.${user.id}${tipoDocumento ? ` AND tipo_documento=eq.${tipoDocumento}` : ''}`
        },
        (payload) => {
          console.log('Cambio en documentos sociales:', payload);
          loadDocumentos();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, tipoDocumento]);

  return {
    documentos,
    loading,
    uploading,
    uploadDocumento,
    deleteDocumento,
    getEstadisticas,
    refetch: loadDocumentos,
  };
};
